<script setup>
import { computed, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import sessionStorage from 'shared/helpers/sessionStorage';
import BillingAPI from 'dashboard/api/billing';

import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import PaymentHistory from './components/PaymentHistory.vue';
import PixPayment from './components/PixPayment.vue';
import PurchaseCreditsModal from './components/PurchaseCreditsModal.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';
import Banner from 'dashboard/components/ui/Banner.vue';

const { currentAccount } = useAccount();
const {
  captainEnabled,
  captainLimits,
  documentLimits,
  responseLimits,
  fetchLimits,
  isFetchingLimits,
} = useCaptain();

const { t } = useI18n();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';
const DEFAULT_BILLING_REMINDER_DAYS = ['7', '3', '1'];

// State for handling refresh attempts and loading
const isWaitingForBilling = ref(false);
const purchaseCreditsModalRef = ref(null);

// Internal billing state
const internalBilling = ref(null);
const internalPayments = ref([]);
const currentInvoice = ref(null);
const internalReminderDays = ref([...DEFAULT_BILLING_REMINDER_DAYS]);

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return customAttributes.value.plan_name;
});

const canPurchaseCredits = computed(() => {
  const plan = planName.value?.toLowerCase();
  return plan && plan !== 'hacker';
});

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    await store.dispatch('accounts/subscription');
  }
  // Always fetch limits for billing page to show credit usage
  fetchLimits();
};

// Internal billing helpers
const hasInternalBilling = computed(
  () => !!internalBilling.value?.subscription
);

const internalPlanName = computed(
  () => internalBilling.value?.plan?.name || '-'
);

const internalPriceCents = computed(
  () => internalBilling.value?.subscription?.price_cents || 0
);

const internalPriceFormatted = computed(
  () => `R$ ${(internalPriceCents.value / 100).toFixed(2)}`
);

const internalStatus = computed(
  () => internalBilling.value?.subscription?.status || '-'
);

const internalNextBilling = computed(() => {
  const date = internalBilling.value?.subscription?.next_billing_date;
  if (!date) return '-';
  return format(new Date(date), 'dd MMM, yyyy');
});

const paymentDueInDays = computed(() => {
  if (!currentInvoice.value?.due_date) return null;

  const dueDate = new Date(currentInvoice.value.due_date);
  const today = new Date();
  dueDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
});

const paymentReminderMessage = computed(() => {
  if (!hasInternalBilling.value || paymentDueInDays.value === null) return '';

  if (paymentDueInDays.value < 0) {
    return t('BILLING_SETTINGS.INTERNAL.PAYMENTS.OVERDUE');
  }

  if (paymentDueInDays.value === 0) {
    return t('BILLING_SETTINGS.INTERNAL.PAYMENTS.DUE_TODAY');
  }

  const reminderDays = internalReminderDays.value
    .map(day => Number(day))
    .filter(day => Number.isInteger(day) && day > 0)
    .sort((a, b) => b - a);

  if (reminderDays.includes(paymentDueInDays.value)) {
    return t('BILLING_SETTINGS.INTERNAL.PAYMENTS.DUE_SOON', {
      days: paymentDueInDays.value,
    });
  }

  return '';
});

const fetchInternalBilling = async () => {
  try {
    const [billingRes, paymentsRes, invoiceRes] = await Promise.all([
      BillingAPI.getBilling(),
      BillingAPI.getPayments(),
      BillingAPI.getCurrentInvoice(),
    ]);
    internalBilling.value = billingRes.data;
    internalPayments.value = paymentsRes.data.payload || [];
    internalReminderDays.value = Array.isArray(billingRes.data.reminder_days)
      ? billingRes.data.reminder_days.map(day => String(day))
      : [...DEFAULT_BILLING_REMINDER_DAYS];
    currentInvoice.value = invoiceRes.data.invoice;
  } catch {
    // Internal billing not configured, that's ok
  }
};

const handleBillingPageLogic = async () => {
  // Check if we've already attempted a refresh for billing setup
  const billingRefreshAttempted = sessionStorage.get(BILLING_REFRESH_ATTEMPTED);

  // If cloud user, fetch account details first
  await fetchAccountDetails();

  await fetchInternalBilling();

  // If internal billing exists, skip cloud billing logic entirely
  if (hasInternalBilling.value) {
    sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
    return;
  }

  // If still no billing plan after fetch (cloud path)
  if (!hasABillingPlan.value) {
    // If we haven't attempted refresh yet, do it once
    if (!billingRefreshAttempted) {
      isWaitingForBilling.value = true;
      sessionStorage.set(BILLING_REFRESH_ATTEMPTED, true);

      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } else {
      // We've already tried refreshing, so just show the no billing message
      // Clear the flag for future visits
      sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
    }
  } else {
    // Billing plan found, clear any existing refresh flag
    sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
  }
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const openPurchaseCreditsModal = () => {
  purchaseCreditsModalRef.value?.open();
};

const handleTopupSuccess = () => {
  // Refresh limits to show updated credit balance
  fetchLimits();
};

onMounted(handleBillingPageLogic);
</script>

<template>
  <SettingsLayout
    :is-loading="
      (uiFlags.isFetchingItem || isWaitingForBilling) && !hasInternalBilling
    "
    :loading-message="
      isWaitingForBilling
        ? $t('BILLING_SETTINGS.NO_BILLING_USER')
        : $t('ATTRIBUTES_MGMT.LOADING')
    "
    :no-records-found="
      !hasABillingPlan && !isWaitingForBilling && !hasInternalBilling
    "
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="
          hasInternalBilling ? '' : $t('BILLING_SETTINGS.VIEW_PRICING')
        "
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <!-- Cloud Billing Section (only when cloud plan exists) -->
        <template v-if="hasABillingPlan">
          <BillingCard
            :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
            :description="
              $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')
            "
          >
            <template #action>
              <ButtonV4 sm solid blue @click="onClickBillingPortal">
                {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
              </ButtonV4>
            </template>
            <div
              v-if="planName || subscribedQuantity || subscriptionRenewsOn"
              class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
            >
              <DetailItem
                :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
                :value="planName"
              />
              <DetailItem
                v-if="subscribedQuantity"
                :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
                :value="subscribedQuantity"
              />
              <DetailItem
                v-if="subscriptionRenewsOn"
                :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
                :value="subscriptionRenewsOn"
              />
            </div>
          </BillingCard>
          <BillingCard
            v-if="captainEnabled"
            :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
            :description="$t('BILLING_SETTINGS.CAPTAIN.DESCRIPTION')"
          >
            <template #action>
              <div class="flex gap-2">
                <ButtonV4
                  sm
                  flushed
                  slate
                  icon="i-lucide-refresh-cw"
                  :is-loading="isFetchingLimits"
                  @click="fetchLimits"
                >
                  {{ $t('BILLING_SETTINGS.CAPTAIN.REFRESH_CREDITS') }}
                </ButtonV4>
                <ButtonV4
                  v-if="canPurchaseCredits"
                  sm
                  solid
                  blue
                  @click="openPurchaseCreditsModal"
                >
                  {{ $t('BILLING_SETTINGS.TOPUP.BUY_CREDITS') }}
                </ButtonV4>
              </div>
            </template>
            <div v-if="captainLimits && responseLimits" class="px-5">
              <BillingMeter
                :title="$t('BILLING_SETTINGS.CAPTAIN.RESPONSES')"
                v-bind="responseLimits"
              />
            </div>
            <div v-if="captainLimits && documentLimits" class="px-5">
              <BillingMeter
                :title="$t('BILLING_SETTINGS.CAPTAIN.DOCUMENTS')"
                v-bind="documentLimits"
              />
            </div>
          </BillingCard>
          <BillingCard
            v-else
            :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
            :description="$t('BILLING_SETTINGS.CAPTAIN.UPGRADE')"
          >
            <template #action>
              <ButtonV4 sm solid slate @click="onClickBillingPortal">
                {{ $t('CAPTAIN.PAYWALL.UPGRADE_NOW') }}
              </ButtonV4>
            </template>
          </BillingCard>
        </template>

        <!-- Internal Billing Section -->
        <BillingCard
          v-if="hasInternalBilling"
          :title="$t('BILLING_SETTINGS.INTERNAL.SUBSCRIPTION.TITLE')"
          :description="
            $t('BILLING_SETTINGS.INTERNAL.SUBSCRIPTION.DESCRIPTION')
          "
        >
          <Banner
            v-if="paymentReminderMessage"
            color-scheme="warning"
            class="mb-4"
            :banner-message="paymentReminderMessage"
          />
          <div
            class="grid grid-cols-2 lg:grid-cols-4 gap-4 px-5 sm:divide-x sm:divide-n-weak sm:gap-2 sm:px-0"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.INTERNAL.SUBSCRIPTION.PLAN')"
              :value="internalPlanName"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.INTERNAL.SUBSCRIPTION.PRICE')"
              :value="internalPriceFormatted"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.INTERNAL.SUBSCRIPTION.STATUS')"
              :value="internalStatus"
            />
            <DetailItem
              :label="$t('BILLING_SETTINGS.INTERNAL.SUBSCRIPTION.NEXT_BILLING')"
              :value="internalNextBilling"
            />
          </div>
        </BillingCard>

        <BillingCard
          v-if="currentInvoice"
          :title="$t('BILLING_SETTINGS.INTERNAL.PIX.TITLE')"
          :description="$t('BILLING_SETTINGS.INTERNAL.PIX.DESCRIPTION')"
        >
          <PixPayment
            :pix-qr-code="currentInvoice.pix_qr_code"
            :pix-copy-paste="currentInvoice.pix_copy_paste"
            :amount-cents="currentInvoice.amount_cents"
            :due-date="currentInvoice.due_date"
          />
        </BillingCard>

        <BillingCard
          v-if="hasInternalBilling"
          :title="$t('BILLING_SETTINGS.INTERNAL.PAYMENTS.TITLE')"
          :description="$t('BILLING_SETTINGS.INTERNAL.PAYMENTS.DESCRIPTION')"
        >
          <PaymentHistory :payments="internalPayments" />
        </BillingCard>

        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
      <PurchaseCreditsModal
        ref="purchaseCreditsModalRef"
        @success="handleTopupSuccess"
      />
    </template>
  </SettingsLayout>
</template>
